import { Procedure, Patient, ValueSetExpansionContains } from '@medplum/fhirtypes'
import { ChartItem } from './PatientChart.utils.tsx'
import { Box, Select, TextInput } from '@mantine/core'
import { DateInput } from '@/components/ui/DateInput'
import { parseDateInputValue } from '@/utils/dateInput.ts'
import { formatDateTime } from '@/utils.ts'
import { ResourceTable } from './ResourceTable.tsx'
import { useState } from 'react'
import { ValueSetAutocomplete, useMedplum } from '@medplum/react'
import { createReference } from '@medplum/core'
import { AddResourceSection } from './AddResourceSection.tsx'

type Props = {
  patient: Patient
  item: ChartItem
}

const PROCEDURE_VALUE_SET = 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113883.3.88.12.80.28'

const STATUS_OPTIONS: { value: NonNullable<Procedure['status']>; label: string }[] = [
  { value: 'preparation', label: 'Preparation' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'not-done', label: 'Not Done' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'completed', label: 'Completed' },
  { value: 'entered-in-error', label: 'Entered in Error' },
  { value: 'unknown', label: 'Unknown' },
]

const availFields = [
  { code: 'procedure', title: 'Procedure' },
  { code: 'date', title: 'Date' },
  { code: 'status', title: 'Status' },
  { code: 'note', title: 'Note' },
  { code: 'indication', title: 'Indication' },
  { code: 'doctor', title: 'Practitioner' },
]

const renderColumn = (resource: Procedure, itemCode: string) => {
  switch (itemCode) {
    case 'procedure': {
      const cpt =
        resource.code?.coding?.find((c) => c.system?.includes('cpt'))?.code ||
        resource.code?.coding?.[0]?.code ||
        '-'
      const label = resource.code?.coding?.[0]?.display || resource.code?.text || '-'
      return (
        <div>
          <div>{label}</div>
          <div className="text-xs text-gray-500">CPT Code: {cpt}</div>
        </div>
      )
    }

    case 'date':
      return resource.performedDateTime
        ? formatDateTime(resource.performedDateTime, '-', true)
        : '-'

    case 'status':
      return resource.status || '-'

    case 'note':
      return resource.note?.map((item) => item.text).join('\n') || '-'

    case 'indication':
      return resource.reasonCode?.map((item) => item.text).join('\n') || '-'

    case 'doctor': {
      let doctor = resource.performer
        ?.map((p) => (typeof p.actor?.display === 'string' && p.actor.display) || '')
        .filter(Boolean)
        .join(', ')

      if (!doctor) {
        doctor =
          resource.extension?.find((e) => e.url.includes('record-recorder'))?.valueReference
            ?.display || '-'
      }
      return doctor || '-'
    }

    default:
      return itemCode
  }
}

export const Procedures = ({ patient, item }: Props) => {
  const medplum = useMedplum()
  const [picked, setPicked] = useState<{ system?: string; code: string; display: string } | null>(
    null,
  )
  const [pickerKey, setPickerKey] = useState(0)
  const [status, setStatus] = useState<string | null>('completed')
  const [performedDate, setPerformedDate] = useState<Date | null>(new Date())
  const [note, setNote] = useState('')

  const resetForm = () => {
    setPicked(null)
    setStatus('completed')
    setPerformedDate(new Date())
    setNote('')
    setPickerKey((k) => k + 1)
  }

  const handlePickerChange = (items: ValueSetExpansionContains[]) => {
    const first = items[0]
    if (!first?.code) {
      setPicked(null)
      return
    }
    setPicked({
      system: first.system,
      code: first.code,
      display: first.display || first.code,
    })
  }

  const buildResource = (): Procedure | { error: string } => {
    if (!picked?.code) return { error: 'Please select a procedure.' }
    const profile = medplum.getProfile()
    return {
      resourceType: 'Procedure',
      status: (status || 'completed') as NonNullable<Procedure['status']>,
      subject: { reference: `Patient/${patient.id}` },
      ...(profile?.resourceType === 'Practitioner' && {
        performer: [{ actor: createReference(profile) }],
      }),
      code: {
        coding: [
          {
            system: picked.system,
            code: picked.code,
            display: picked.display,
          },
        ],
        text: picked.display,
      },
      performedDateTime: performedDate ? performedDate.toISOString() : undefined,
      note: note.trim() ? [{ text: note.trim() }] : undefined,
    }
  }

  return (
    <AddResourceSection<Procedure>
      patient={patient}
      title="Procedures"
      modalTitle="Add Procedure"
      resourceType="Procedure"
      featureContext="Procedure"
      noun="procedure"
      isSubmitDisabled={!picked?.code}
      resetForm={resetForm}
      buildResource={buildResource}
      renderForm={() => (
        <>
          <Box>
            <p className="mb-1 text-sm font-medium">Procedure (CPT) *</p>
            <ValueSetAutocomplete
              key={pickerKey}
              name="add-procedure"
              placeholder="Search procedure"
              binding={PROCEDURE_VALUE_SET}
              maxValues={1}
              clearable={false}
              onChange={handlePickerChange}
            />
            {picked && (
              <p className="mt-1 text-xs text-gray-500">
                Selected: {picked.display} ({picked.code})
              </p>
            )}
          </Box>
          <DateInput
            label="Date Performed"
            value={performedDate}
            onChange={(value) => setPerformedDate(parseDateInputValue(value))}
            clearable
            placeholder="Pick a date"
          />
          <Select label="Status" data={STATUS_OPTIONS} value={status} onChange={setStatus} />
          <TextInput
            label="Note"
            placeholder="Optional note"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
        </>
      )}
    >
      {(refreshKey) => (
        <ResourceTable
          key={refreshKey}
          patient={patient}
          item={item}
          availFields={availFields}
          renderColumn={renderColumn}
          defaultSort="date"
          blockRemove
        />
      )}
    </AddResourceSection>
  )
}
